import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import Modal from "../Modal";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "../../store/store";
import { Typography } from "@material-tailwind/react";
import moment from "moment";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import { AllBookingList, exportAllBookings } from "../../store/BookingSlice";
import { AllBookingLists } from "../../interfaces/BookingList";

const ListView: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  // State
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "free">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState<AllBookingLists | null>(null);
  const [copiedRef, setCopiedRef] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const limit = 10;

  // Redux state
  const bookingState = useSelector((state: RootState) => state.booking);
  const bookingData = (bookingState.data || []) as AllBookingLists[];
  const total = bookingState.total || bookingData.length || 0;
  const totalPages = bookingState.totalPages || Math.max(1, Math.ceil(total / limit));
  const loading = bookingState.loading;
  const stats = bookingState.stats;

  // Debounced search handler
  const debouncedSearchHandler = useRef(
    debounce((query: string) => {
      setDebouncedSearch(query);
      setCurrentPage(1);
    }, 350)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [debouncedSearchHandler]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSearchHandler(val);
  };

  const handleClearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    debouncedSearchHandler.cancel();
    setCurrentPage(1);
  };

  // Fetch bookings whenever page, debouncedSearch, or statusFilter changes
  const fetchBookings = useCallback(() => {
    dispatch(AllBookingList({ page: currentPage, limit, search: debouncedSearch, status: statusFilter }));
  }, [dispatch, currentPage, limit, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleStatusFilterChange = (newStatus: "all" | "paid" | "free") => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  // Displayed bookings are now directly provided by the backend (filtered & paginated)
  const displayedBookings = bookingData;

  // Copy reference ID handler
  const handleCopyRef = (refId: string) => {
    if (!refId) return;
    navigator.clipboard.writeText(refId);
    setCopiedRef(refId);
    toast.info(t("adminBookings.copiedRef", "Reference ID copied to clipboard"));
    setTimeout(() => setCopiedRef(null), 2500);
  };

  // Export to CSV handler
  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const allBookings = await dispatch(exportAllBookings()).unwrap();
      if (!allBookings || allBookings.length === 0) {
        toast.info(t("adminBookings.noDataExport", "No bookings found to export"));
        return;
      }

      const headers = [
        "Booking ID",
        "Reference Number",
        "Patient Name",
        "Patient Email",
        "Patient Phone",
        "Program Title",
        "Amount (USD)",
        "Booking Date",
        "Status",
        "Payment Method",
      ];

      const csvRows = allBookings.map((b) => {
        const isFree = (b.amount ?? 0) === 0 || b.isFree;
        const statusText = isFree ? "Free Access" : "Paid & Confirmed";
        const dateText = b.paymentDate ? moment(b.paymentDate).format("YYYY-MM-DD HH:mm") : "";
        return [
          `"${(b.id || "").replace(/"/g, '""')}"`,
          `"${(b.paymentReferenceId || "").replace(/"/g, '""')}"`,
          `"${(b.patient?.fullName || "").replace(/"/g, '""')}"`,
          `"${(b.patient?.email || "").replace(/"/g, '""')}"`,
          `"${(b.patient?.phoneNo || "").replace(/"/g, '""')}"`,
          `"${(b.program?.title || "").replace(/"/g, '""')}"`,
          `"${b.amount != null ? b.amount : 0}"`,
          `"${dateText}"`,
          `"${statusText}"`,
          `"${(b.paymentMethod || (isFree ? "Free Access Grant" : "Stripe")).replace(/"/g, '""')}"`,
        ].join(",");
      });

      const csvContent = "\uFEFF" + [headers.join(","), ...csvRows].join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bookings_export_${moment().format("YYYY-MM-DD")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(t("adminBookings.exportSuccess", "Bookings exported successfully!"));
    } catch (err: any) {
      toast.error(err || t("adminBookings.exportError", "Failed to export bookings"));
    } finally {
      setIsExporting(false);
    }
  };

  const TABLE_HEAD = [
    t("adminBookings.table.index", "#"),
    t("adminBookings.table.patientName", "PATIENT / CONTACT"),
    t("adminBookings.table.title", "PROGRAM ENROLLED"),
    t("adminBookings.table.amount", "AMOUNT"),
    t("adminBookings.table.status", "STATUS"),
    t("adminBookings.table.bookingDate", "BOOKING DATE"),
    t("adminBookings.table.acknowledgementNumber", "ACKNOWLEDGEMENT NUMBER"),
    t("adminBookings.table.actions", "ACTIONS"),
  ];

  return (
    <div className="w-full flex-1 min-h-0 flex flex-col overflow-hidden bg-[#FAFBFC]">
      {/* Search & Stats Toolbar */}
      <div className="p-3 sm:p-4 md:p-5 bg-white border-b border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4 shrink-0">
        {/* Search Bar */}
        <div className="relative w-full lg:max-w-md">
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder={t("adminBookings.searchPlaceholder", "Search by patient, program, or reference...")}
            className="w-full pl-10 pr-9 py-2.5 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm text-[#1E293B] placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#B4804A] focus:ring-2 focus:ring-[#B4804A]/15 transition-all"
          />
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {search && (
            <button
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Status Filter Tabs & Export CSV */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full lg:w-auto">
          {/* Status Tabs */}
          <div className="grid grid-cols-3 sm:flex p-1 bg-gray-100/80 rounded-xl border border-gray-200/60 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => handleStatusFilterChange("all")}
              className={`px-2.5 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                statusFilter === "all"
                  ? "bg-white text-gray-900 shadow-xs"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <span>{t("adminBookings.filterAll", "All")}</span>
              <span className="ml-1 sm:ml-1.5 px-1.5 py-0.2 bg-gray-200/60 text-gray-700 rounded-md text-[10px] sm:text-[11px]">
                {stats?.totalBookings ?? total}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleStatusFilterChange("paid")}
              className={`px-2.5 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                statusFilter === "paid"
                  ? "bg-white text-emerald-700 shadow-xs"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <span>{t("adminBookings.filterPaid", "Paid")}</span>
              {stats?.paidCount != null && (
                <span className="ml-1 sm:ml-1.5 px-1.5 py-0.2 bg-emerald-100/60 text-emerald-800 rounded-md text-[10px] sm:text-[11px]">
                  {stats.paidCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleStatusFilterChange("free")}
              className={`px-2.5 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                statusFilter === "free"
                  ? "bg-white text-purple-700 shadow-xs"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <span className="truncate">{t("adminBookings.filterFree", "Free")}</span>
              {stats?.freeCount != null && (
                <span className="ml-1 sm:ml-1.5 px-1.5 py-0.2 bg-purple-100/60 text-purple-800 rounded-md text-[10px] sm:text-[11px]">
                  {stats.freeCount}
                </span>
              )}
            </button>
          </div>

          {/* Export to CSV Button */}
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={isExporting}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-200 text-gray-700 rounded-xl text-xs font-semibold shadow-2xs hover:shadow-xs transition-all cursor-pointer disabled:opacity-50 w-full sm:w-auto"
            title={t("adminBookings.exportCsvTooltip", "Export all bookings to CSV")}
          >
            {isExporting ? (
              <div className="w-3.5 h-3.5 border-2 border-[#B4804A] border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            <span>{isExporting ? t("adminBookings.exporting", "Exporting...") : t("adminBookings.exportCsv", "Export CSV")}</span>
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="w-full flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full min-w-[880px] table-auto text-left">
          <thead className="sticky top-0 bg-white z-10 shadow-2xs">
            <tr className="bg-[#F8F9FA] text-left border-b-2 border-gray-100">
              {TABLE_HEAD.map((head, i) => (
                <th
                  key={i}
                  className="py-3 sm:py-3.5 px-3.5 sm:px-6 font-bold text-[#7E8299] uppercase text-xs whitespace-nowrap tracking-wider"
                >
                  <Typography
                    variant="small"
                    className="font-semibold leading-none text-[#7E8299] opacity-75 whitespace-nowrap text-xs"
                  >
                    {head}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-16">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div
                      className="border-3 border-solid h-8 w-8 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: "#B4804A", borderTopColor: "transparent" }}
                    />
                    <span className="text-xs font-semibold text-gray-400">
                      {t("adminBookings.loading", "Loading bookings...")}
                    </span>
                  </div>
                </td>
              </tr>
            ) : !displayedBookings || displayedBookings.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-16 text-gray-500 font-medium">
                  <div className="max-w-sm mx-auto flex flex-col items-center gap-2 px-4">
                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 mb-1">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-700 font-semibold text-sm">
                      {t("adminBookings.noBookings", "No bookings found")}
                    </p>
                    {search ? (
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-xs text-gray-400 text-center">
                          No matches found for &quot;{search}&quot;.
                        </p>
                        <button
                          type="button"
                          onClick={handleClearSearch}
                          className="text-xs font-semibold text-[#B4804A] hover:underline cursor-pointer"
                        >
                          Clear search query
                        </button>
                      </div>
                    ) : statusFilter !== "all" ? (
                      <button
                        type="button"
                        onClick={() => setStatusFilter("all")}
                        className="text-xs font-semibold text-[#B4804A] hover:underline cursor-pointer"
                      >
                        Reset filters to view all bookings
                      </button>
                    ) : (
                      <p className="text-xs text-gray-400 text-center">
                        When users book or enroll into programs, their records will appear here.
                      </p>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              displayedBookings.map((booking, index) => {
                const isFree = (booking.amount ?? 0) === 0 || booking.isFree;
                return (
                  <tr
                    key={booking.id || index}
                    className="hover:bg-amber-50/20 transition-colors border-b border-gray-50"
                  >
                    {/* Index */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6 whitespace-nowrap text-xs text-gray-400 font-medium">
                      {(currentPage - 1) * limit + index + 1}
                    </td>

                    {/* Patient Contact Info */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 text-sm">
                          {booking?.patient?.fullName || "—"}
                        </span>
                        {booking?.patient?.email ? (
                          <a
                            href={`mailto:${booking.patient.email}`}
                            className="text-xs text-[#B4804A] hover:underline flex items-center gap-1 mt-0.5"
                          >
                            <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="truncate max-w-[180px] sm:max-w-xs">{booking.patient.email}</span>
                          </a>
                        ) : null}
                      </div>
                    </td>

                    {/* Program Title */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6">
                      <span className="font-medium text-gray-800 text-sm line-clamp-1 max-w-xs" title={booking?.program?.title}>
                        {booking?.program?.title || "—"}
                      </span>
                    </td>

                    {/* Amount */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6 whitespace-nowrap">
                      {isFree ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          $ 0
                        </span>
                      ) : (
                        <span className="font-bold text-gray-900 text-sm">
                          $ {booking?.amount != null ? booking.amount : "—"}
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6 whitespace-nowrap">
                      {isFree ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                          {t("adminBookings.freeAccessBadge", "Free Access")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                          {t("adminBookings.paidBadge", "Paid")}
                        </span>
                      )}
                    </td>

                    {/* Booking Date */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6 whitespace-nowrap text-sm text-gray-500">
                      {booking?.paymentDate
                        ? moment(booking.paymentDate).format("DD MMM, YYYY")
                        : "—"}
                    </td>

                    {/* Acknowledgement / Ref Number */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6 whitespace-nowrap">
                      {booking?.paymentReferenceId ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md border border-gray-200 max-w-[130px] sm:max-w-[160px] truncate" title={booking.paymentReferenceId}>
                            {booking.paymentReferenceId}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopyRef(booking.paymentReferenceId)}
                            className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer shrink-0"
                            title={t("adminBookings.copyRefTooltip", "Copy reference ID")}
                          >
                            {copiedRef === booking.paymentReferenceId ? (
                              <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="py-3 sm:py-4 px-3.5 sm:px-6 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setSelectedBooking(booking)}
                        className="p-1.5 text-gray-500 hover:text-[#041B44] hover:bg-gray-100 rounded-md transition-colors cursor-pointer inline-flex items-center gap-1"
                        title={t("adminBookings.viewDetails", "View Booking Receipt & Details")}
                        aria-label="View booking details"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-200/80 px-3 sm:px-6 py-3 bg-white gap-2.5 sm:gap-2 shrink-0">
        <span className="text-xs text-gray-500 font-medium text-center sm:text-left">
          Showing page <span className="font-bold text-gray-800">{currentPage}</span> of{" "}
          <span className="font-bold text-gray-800">{totalPages || 1}</span>{" "}
          <span className="text-gray-400">({total} total {total === 1 ? "booking" : "bookings"})</span>
        </span>

        <div className="flex items-center justify-center gap-1.5 w-full sm:w-auto overflow-x-auto py-0.5">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1 shrink-0 ${
              currentPage === 1
                ? "text-gray-300 border-gray-200 cursor-not-allowed bg-gray-50"
                : "text-gray-700 border-gray-300 hover:bg-gray-50 active:scale-98 cursor-pointer"
            }`}
          >
            ← {t("common.previous", "Previous")}
          </button>

          {Array.from({ length: totalPages || 1 }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .map((p, idx, arr) => (
              <React.Fragment key={p}>
                {idx > 0 && arr[idx - 1] !== p - 1 && (
                  <span className="px-0.5 text-gray-400 text-xs">...</span>
                )}
                <button
                  type="button"
                  onClick={() => setCurrentPage(p)}
                  className={`min-w-[28px] h-7 px-2 text-xs font-bold rounded-lg border transition-all cursor-pointer shrink-0 ${
                    currentPage === p
                      ? "text-[#037897] bg-[#EBF7F9] border-[#037897]/20 shadow-xs"
                      : "text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              </React.Fragment>
            ))}

          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= (totalPages || 1)}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1 shrink-0 ${
              currentPage >= (totalPages || 1)
                ? "text-gray-300 border-gray-200 cursor-not-allowed bg-gray-50"
                : "text-gray-700 border-gray-300 hover:bg-gray-50 active:scale-98 cursor-pointer"
            }`}
          >
            {t("common.next", "Next")} →
          </button>
        </div>
      </div>

      {/* Booking Details & Receipt Modal - Reusable Mobile-First Component */}
      <Modal
        isOpen={Boolean(selectedBooking)}
        onClose={() => setSelectedBooking(null)}
        title={t("adminBookings.modal.title", "Booking Details & Receipt")}
        subtitle={selectedBooking?.paymentReferenceId || selectedBooking?.id}
        size="lg"
        headerTheme="navy"
        footer={
          selectedBooking && (
            <>
              <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
                {selectedBooking.paymentReferenceId ? (
                  <button
                    type="button"
                    onClick={() => handleCopyRef(selectedBooking.paymentReferenceId)}
                    className="px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200/70 border border-gray-200 sm:border-transparent rounded-xl transition-colors cursor-pointer inline-flex items-center justify-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="truncate">{t("adminBookings.modal.copyRef", "Copy Ref")}</span>
                  </button>
                ) : <div />}

                <button
                  type="button"
                  onClick={() => window.print()}
                  className="px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200/70 border border-gray-200 sm:border-transparent rounded-xl transition-colors cursor-pointer inline-flex items-center justify-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>{t("adminBookings.modal.print", "Print")}</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setSelectedBooking(null)}
                className="w-full sm:w-auto px-5 py-2.5 sm:py-2 bg-[#041B44] hover:bg-[#031535] text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer shadow-xs text-center"
              >
                {t("common.close", "Close")}
              </button>
            </>
          )
        }
      >
        {selectedBooking && (
          <>
            {/* Highlight Card */}
            <div className="p-3 sm:p-4 bg-gradient-to-br from-[#F8FAFC] to-[#EDF2F6] rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4">
              <div className="min-w-0 flex-1">
                <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                  {t("adminBookings.modal.programLabel", "Enrolled Program")}
                </span>
                <h4 className="font-bold text-gray-900 text-sm sm:text-base mt-0.5 leading-snug break-words">
                  {selectedBooking.program?.title || "—"}
                </h4>
              </div>
              <div className="sm:text-right shrink-0 flex sm:flex-col items-center sm:items-end justify-between border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-200/80">
                <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                  {t("adminBookings.modal.amountLabel", "Total Paid")}
                </span>
                <span className="text-lg sm:text-xl font-bold text-gray-900">
                  {(selectedBooking.amount ?? 0) === 0 || selectedBooking.isFree
                    ? "$ 0"
                    : `$ ${selectedBooking.amount}`}
                </span>
              </div>
            </div>

            {/* Patient Information Section */}
            <div className="border border-gray-200 rounded-xl p-3 sm:p-4 bg-white space-y-2 sm:space-y-3">
              <h5 className="text-[11px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2">
                {t("adminBookings.modal.patientDetails", "Patient Information")}
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-sm">
                <div>
                  <span className="text-[11px] sm:text-xs text-gray-400 block font-medium">
                    {t("adminBookings.modal.patientName", "Full Name")}
                  </span>
                  <span className="font-semibold text-gray-800 text-xs sm:text-sm break-words">
                    {selectedBooking.patient?.fullName || "—"}
                  </span>
                </div>

                <div>
                  <span className="text-[11px] sm:text-xs text-gray-400 block font-medium">
                    {t("adminBookings.modal.patientEmail", "Email Address")}
                  </span>
                  {selectedBooking.patient?.email ? (
                    <a
                      href={`mailto:${selectedBooking.patient.email}`}
                      className="text-xs font-semibold text-[#B4804A] hover:underline break-all block"
                    >
                      {selectedBooking.patient.email}
                    </a>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </div>

                {selectedBooking.patient?.phoneNo && (
                  <div>
                    <span className="text-[11px] sm:text-xs text-gray-400 block font-medium">
                      {t("adminBookings.modal.phone", "Phone Number")}
                    </span>
                    <span className="font-mono text-xs text-gray-700">
                      {selectedBooking.patient.phoneNo}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Transaction & Receipt Section */}
            <div className="border border-gray-200 rounded-xl p-3 sm:p-4 bg-white space-y-2 sm:space-y-3">
              <h5 className="text-[11px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2">
                {t("adminBookings.modal.transactionDetails", "Transaction & Payment Information")}
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-sm">
                <div>
                  <span className="text-[11px] sm:text-xs text-gray-400 block font-medium">
                    {t("adminBookings.modal.referenceId", "Reference ID")}
                  </span>
                  <span className="font-mono text-[11px] sm:text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-800 inline-block break-all max-w-full">
                    {selectedBooking.paymentReferenceId || "—"}
                  </span>
                </div>

                <div>
                  <span className="text-[11px] sm:text-xs text-gray-400 block font-medium">
                    {t("adminBookings.modal.date", "Payment Date")}
                  </span>
                  <span className="text-xs text-gray-700 font-medium">
                    {selectedBooking.paymentDate
                      ? moment(selectedBooking.paymentDate).format("DD MMMM YYYY, HH:mm")
                      : "—"}
                  </span>
                </div>

                <div>
                  <span className="text-[11px] sm:text-xs text-gray-400 block font-medium">
                    {t("adminBookings.modal.paymentMethod", "Payment Method")}
                  </span>
                  <span className="text-xs font-semibold text-gray-700 capitalize">
                    {selectedBooking.paymentMethod ||
                      ((selectedBooking.amount ?? 0) === 0 || selectedBooking.isFree
                        ? "Free Access Grant"
                        : "Stripe")}
                  </span>
                </div>

                <div>
                  <span className="text-[11px] sm:text-xs text-gray-400 block font-medium">
                    {t("adminBookings.modal.enrollmentStatus", "Enrollment Status")}
                  </span>
                  {(selectedBooking.amount ?? 0) === 0 || selectedBooking.isFree ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                      Free Access
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      Confirmed & Paid
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ListView;
